import { useState, useEffect } from 'react';
import { useAuth, useBusinessUnit } from '@/App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { 
  BarChart3, PieChart, TrendingUp, Download, Calendar, Users, 
  DollarSign, FileText, Building2, Filter, RefreshCw, Settings2, FileSpreadsheet, FileDown
} from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart as RePieChart, Pie, Cell, LineChart, Line, AreaChart, Area } from 'recharts';
import axios from 'axios';
import { formatCurrency } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16'];

// Available report columns for custom reports
const REPORT_COLUMNS = [
  { key: 'statement_date', label: 'Statement Date', type: 'date' },
  { key: 'agent_name', label: 'Agent Name', type: 'text' },
  { key: 'co_agent_name', label: 'Co-Agent Name', type: 'text' },
  { key: 'lender', label: 'Lender', type: 'text' },
  { key: 'product', label: 'Product', type: 'text' },
  { key: 'description', label: 'Description', type: 'text' },
  { key: 'mortgage_loan', label: 'Loan Amount', type: 'currency' },
  { key: 'gross_commission', label: 'Gross Commission', type: 'currency' },
  { key: 'agent_commission', label: 'Agent Commission', type: 'currency' },
  { key: 'co_agent_commission', label: 'Co-Agent Commission', type: 'currency' },
  { key: 'brokerage_commission', label: 'Brokerage Commission', type: 'currency' },
  { key: 'royalty_plus_gst', label: 'Royalty + GST', type: 'currency' },
  { key: 'net_commission', label: 'Net Commission', type: 'currency' },
];

export default function Reports() {
  const { user } = useAuth();
  const { businessUnit } = useBusinessUnit();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().getFullYear(), 0, 1).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  
  // Report data
  const [commissions, setCommissions] = useState([]);
  const [agents, setAgents] = useState([]);
  const [referrals, setReferrals] = useState([]);
  
  // Custom report builder state
  const [selectedColumns, setSelectedColumns] = useState(['statement_date', 'agent_name', 'gross_commission', 'agent_commission', 'brokerage_commission']);
  const [groupBy, setGroupBy] = useState('none');
  const [sortBy, setSortBy] = useState('statement_date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [agentFilter, setAgentFilter] = useState('all');
  const [lenderFilter, setLenderFilter] = useState('all');
  
  const isAdmin = user?.role === 'admin';
  const buPrefix = businessUnit === 'mortgage' ? '' : '/insurance';

  useEffect(() => {
    fetchData();
  }, [businessUnit, dateRange]);

  const fetchData = async () => {
    setLoading(true);
    const token = getToken();
    try {
      const [commissionsRes, agentsRes, referralsRes] = await Promise.all([
        axios.get(`${API}/api${buPrefix}/commissions`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api${buPrefix}/agents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/api/referrals`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      
      // Filter by date range
      const filteredCommissions = commissionsRes.data.filter(c => {
        const date = new Date(c.statement_date);
        return date >= new Date(dateRange.startDate) && date <= new Date(dateRange.endDate);
      });
      
      setCommissions(filteredCommissions);
      setAgents(agentsRes.data);
      setReferrals(referralsRes.data);
    } catch (error) {
      toast.error('Failed to load report data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate metrics
  const metrics = {
    totalCommissions: commissions.reduce((sum, c) => sum + (c.gross_commission || 0), 0),
    totalAgentEarnings: commissions.reduce((sum, c) => sum + (c.agent_commission || 0), 0),
    totalBrokerageEarnings: commissions.reduce((sum, c) => sum + (c.brokerage_commission || 0), 0),
    totalRoyalty: commissions.reduce((sum, c) => sum + (c.royalty_plus_gst || 0), 0),
    totalDeals: commissions.length,
    averageDealSize: commissions.length > 0 
      ? commissions.reduce((sum, c) => sum + (c.mortgage_loan || c.premium_amount || 0), 0) / commissions.length 
      : 0,
    totalReferrals: referrals.length,
    successfulReferrals: referrals.filter(r => r.status === 'Success').length
  };

  // Agent performance data
  const agentPerformance = agents.map(agent => {
    const agentCommissions = commissions.filter(c => c.agent_id === agent.id);
    return {
      name: agent.name,
      deals: agentCommissions.length,
      totalEarnings: agentCommissions.reduce((sum, c) => sum + (c.agent_commission || 0), 0),
      totalVolume: agentCommissions.reduce((sum, c) => sum + (c.mortgage_loan || c.premium_amount || 0), 0)
    };
  }).sort((a, b) => b.totalEarnings - a.totalEarnings);

  // Monthly trend data
  const monthlyData = [];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  
  commissions.forEach(c => {
    const date = new Date(c.statement_date);
    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    const existing = monthlyData.find(m => m.monthKey === monthKey);
    
    if (existing) {
      existing.gross += c.gross_commission || 0;
      existing.agent += c.agent_commission || 0;
      existing.brokerage += c.brokerage_commission || 0;
      existing.deals += 1;
    } else {
      monthlyData.push({
        monthKey,
        month: monthNames[date.getMonth()],
        year: date.getFullYear(),
        gross: c.gross_commission || 0,
        agent: c.agent_commission || 0,
        brokerage: c.brokerage_commission || 0,
        deals: 1
      });
    }
  });
  
  monthlyData.sort((a, b) => a.monthKey.localeCompare(b.monthKey));

  // Lender distribution (for mortgage) or Product distribution (for insurance)
  const distributionData = {};
  commissions.forEach(c => {
    const key = c.lender || c.carrier || 'Unknown';
    if (!distributionData[key]) {
      distributionData[key] = { name: key, value: 0, deals: 0 };
    }
    distributionData[key].value += c.gross_commission || 0;
    distributionData[key].deals += 1;
  });
  const pieData = Object.values(distributionData).sort((a, b) => b.value - a.value).slice(0, 8);

  // Export to CSV
  const exportToCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('No data to export');
      return;
    }
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => headers.map(h => {
        const val = row[h];
        if (typeof val === 'string' && val.includes(',')) {
          return `"${val}"`;
        }
        return val;
      }).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Report exported successfully');
  };

  // Export to PDF
  const exportToPDF = async (reportType) => {
    const token = getToken();
    const division = businessUnit;
    
    try {
      toast.loading('Generating PDF...');
      const response = await axios.get(
        `${API}/api/reports/${reportType}/pdf`,
        {
          params: {
            start_date: dateRange.startDate,
            end_date: dateRange.endDate,
            division: division
          },
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `${reportType}_${division}_${dateRange.startDate}_to_${dateRange.endDate}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
      
      toast.dismiss();
      toast.success('PDF exported successfully');
    } catch (error) {
      toast.dismiss();
      toast.error('Failed to generate PDF');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <BarChart3 className="w-7 h-7" />
            Advanced Reports
          </h1>
          <p className="text-slate-500">
            {businessUnit === 'mortgage' ? 'Mortgage' : 'Insurance & Investment'} Division Analytics
          </p>
        </div>
        
        {/* Date Range Filter */}
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-slate-500" />
            <Input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange({ ...dateRange, startDate: e.target.value })}
              className="w-36"
            />
            <span className="text-slate-400">to</span>
            <Input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange({ ...dateRange, endDate: e.target.value })}
              className="w-36"
            />
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(metrics.totalCommissions)}</p>
                <p className="text-xs text-slate-500">Total Gross</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <Users className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(metrics.totalAgentEarnings)}</p>
                <p className="text-xs text-slate-500">Agent Earnings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Building2 className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{formatCurrency(metrics.totalBrokerageEarnings)}</p>
                <p className="text-xs text-slate-500">Brokerage Earnings</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <FileText className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{metrics.totalDeals}</p>
                <p className="text-xs text-slate-500">Total Deals</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="agents">Agent Performance</TabsTrigger>
          <TabsTrigger value="trends">Monthly Trends</TabsTrigger>
          <TabsTrigger value="distribution">Distribution</TabsTrigger>
          <TabsTrigger value="custom">
            <Settings2 className="w-4 h-4 mr-1" />
            Custom Report
          </TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-4">
          <div className="grid md:grid-cols-2 gap-6">
            {/* Monthly Revenue Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Monthly Revenue</CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={monthlyData}>
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="month" />
                    <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                    <Bar dataKey="gross" name="Gross" fill="#3b82f6" />
                    <Bar dataKey="agent" name="Agent" fill="#10b981" />
                    <Bar dataKey="brokerage" name="Brokerage" fill="#f59e0b" />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {/* Pie Chart */}
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">
                  {businessUnit === 'mortgage' ? 'Lender' : 'Product'} Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <RePieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                  </RePieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </div>

          {/* Summary Stats */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Period Summary</CardTitle>
              <CardDescription>
                {dateRange.startDate} to {dateRange.endDate}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Average Deal Size</p>
                  <p className="text-xl font-bold">{formatCurrency(metrics.averageDealSize)}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Total Royalty + GST</p>
                  <p className="text-xl font-bold">{formatCurrency(metrics.totalRoyalty)}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Referrals</p>
                  <p className="text-xl font-bold">{metrics.totalReferrals}</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-500">Successful Referrals</p>
                  <p className="text-xl font-bold text-emerald-600">{metrics.successfulReferrals}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Export PDF Button */}
          <div className="flex justify-end">
            <Button onClick={() => exportToPDF('commission-summary')} className="bg-red-600 hover:bg-red-700">
              <FileDown className="w-4 h-4 mr-2" />
              Export Full Report to PDF
            </Button>
          </div>
        </TabsContent>

        {/* Agent Performance Tab */}
        <TabsContent value="agents">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Agent Performance</CardTitle>
                <CardDescription>Ranked by total earnings</CardDescription>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => exportToCSV(agentPerformance, 'agent_performance')}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
                <Button onClick={() => exportToPDF('agent-performance')} className="bg-red-600 hover:bg-red-700">
                  <FileDown className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Rank</TableHead>
                    <TableHead>Agent</TableHead>
                    <TableHead className="text-right">Deals</TableHead>
                    <TableHead className="text-right">Total Volume</TableHead>
                    <TableHead className="text-right">Total Earnings</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {agentPerformance.map((agent, idx) => (
                    <TableRow key={agent.name}>
                      <TableCell>
                        <Badge variant={idx < 3 ? 'default' : 'secondary'}>
                          #{idx + 1}
                        </Badge>
                      </TableCell>
                      <TableCell className="font-medium">{agent.name}</TableCell>
                      <TableCell className="text-right">{agent.deals}</TableCell>
                      <TableCell className="text-right">{formatCurrency(agent.totalVolume)}</TableCell>
                      <TableCell className="text-right font-bold text-emerald-600">
                        {formatCurrency(agent.totalEarnings)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Monthly Trends Tab */}
        <TabsContent value="trends">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">Monthly Trends</CardTitle>
                <CardDescription>Commission trends over time</CardDescription>
              </div>
              <Button variant="outline" onClick={() => exportToCSV(monthlyData, 'monthly_trends')}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <ResponsiveContainer width="100%" height={400}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis tickFormatter={(v) => `$${(v/1000).toFixed(0)}k`} />
                  <Tooltip formatter={(v) => formatCurrency(v)} />
                  <Legend />
                  <Line type="monotone" dataKey="gross" name="Gross Commission" stroke="#3b82f6" strokeWidth={2} />
                  <Line type="monotone" dataKey="agent" name="Agent Earnings" stroke="#10b981" strokeWidth={2} />
                  <Line type="monotone" dataKey="brokerage" name="Brokerage" stroke="#f59e0b" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>

              <Table className="mt-6">
                <TableHeader>
                  <TableRow>
                    <TableHead>Month</TableHead>
                    <TableHead className="text-right">Deals</TableHead>
                    <TableHead className="text-right">Gross</TableHead>
                    <TableHead className="text-right">Agent</TableHead>
                    <TableHead className="text-right">Brokerage</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {monthlyData.map((month) => (
                    <TableRow key={month.monthKey}>
                      <TableCell className="font-medium">{month.month} {month.year}</TableCell>
                      <TableCell className="text-right">{month.deals}</TableCell>
                      <TableCell className="text-right">{formatCurrency(month.gross)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(month.agent)}</TableCell>
                      <TableCell className="text-right">{formatCurrency(month.brokerage)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Distribution Tab */}
        <TabsContent value="distribution">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="text-lg">
                  {businessUnit === 'mortgage' ? 'Lender' : 'Product'} Distribution
                </CardTitle>
                <CardDescription>Commission breakdown by source</CardDescription>
              </div>
              <Button variant="outline" onClick={() => exportToCSV(pieData, 'distribution')}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-6">
                <ResponsiveContainer width="100%" height={350}>
                  <RePieChart>
                    <Pie
                      data={pieData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      outerRadius={120}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {pieData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => formatCurrency(v)} />
                    <Legend />
                  </RePieChart>
                </ResponsiveContainer>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{businessUnit === 'mortgage' ? 'Lender' : 'Product'}</TableHead>
                      <TableHead className="text-right">Deals</TableHead>
                      <TableHead className="text-right">Commission</TableHead>
                      <TableHead className="text-right">Share</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pieData.map((item, idx) => {
                      const total = pieData.reduce((sum, p) => sum + p.value, 0);
                      const share = total > 0 ? (item.value / total * 100).toFixed(1) : 0;
                      return (
                        <TableRow key={item.name}>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-3 h-3 rounded-full" 
                                style={{ backgroundColor: COLORS[idx % COLORS.length] }}
                              />
                              {item.name}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{item.deals}</TableCell>
                          <TableCell className="text-right">{formatCurrency(item.value)}</TableCell>
                          <TableCell className="text-right">{share}%</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Custom Report Builder Tab */}
        <TabsContent value="custom" className="space-y-4">
          <Card>
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileSpreadsheet className="w-5 h-5" />
                    Custom Report Builder
                  </CardTitle>
                  <CardDescription>Select columns, filter data, and generate custom reports</CardDescription>
                </div>
                <Button 
                  onClick={() => {
                    // Build custom report data based on selections
                    let reportData = [...commissions];
                    
                    // Apply filters
                    if (agentFilter !== 'all') {
                      reportData = reportData.filter(c => c.agent_id === agentFilter);
                    }
                    if (lenderFilter !== 'all') {
                      reportData = reportData.filter(c => (c.lender || c.carrier) === lenderFilter);
                    }
                    
                    // Sort
                    reportData.sort((a, b) => {
                      const aVal = a[sortBy] || '';
                      const bVal = b[sortBy] || '';
                      if (sortOrder === 'asc') {
                        return aVal > bVal ? 1 : -1;
                      }
                      return aVal < bVal ? 1 : -1;
                    });
                    
                    // Build export data with selected columns only
                    const exportData = reportData.map(row => {
                      const newRow = {};
                      selectedColumns.forEach(col => {
                        const colDef = REPORT_COLUMNS.find(c => c.key === col);
                        newRow[colDef?.label || col] = row[col] || '';
                      });
                      return newRow;
                    });
                    
                    exportToCSV(exportData, 'custom_report');
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Export Custom Report
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Column Selection */}
              <div>
                <Label className="text-sm font-medium mb-2 block">Select Columns to Include</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {REPORT_COLUMNS.map(col => (
                    <div key={col.key} className="flex items-center space-x-2">
                      <Checkbox 
                        id={col.key}
                        checked={selectedColumns.includes(col.key)}
                        onCheckedChange={(checked) => {
                          if (checked) {
                            setSelectedColumns([...selectedColumns, col.key]);
                          } else {
                            setSelectedColumns(selectedColumns.filter(c => c !== col.key));
                          }
                        }}
                      />
                      <label htmlFor={col.key} className="text-sm cursor-pointer">{col.label}</label>
                    </div>
                  ))}
                </div>
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div>
                  <Label className="text-sm font-medium mb-2 block">Filter by Agent</Label>
                  <Select value={agentFilter} onValueChange={setAgentFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder="All Agents" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Agents</SelectItem>
                      {agents.map(agent => (
                        <SelectItem key={agent.id} value={agent.id}>{agent.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label className="text-sm font-medium mb-2 block">Filter by {businessUnit === 'mortgage' ? 'Lender' : 'Carrier'}</Label>
                  <Select value={lenderFilter} onValueChange={setLenderFilter}>
                    <SelectTrigger>
                      <SelectValue placeholder={`All ${businessUnit === 'mortgage' ? 'Lenders' : 'Carriers'}`} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All {businessUnit === 'mortgage' ? 'Lenders' : 'Carriers'}</SelectItem>
                      {[...new Set(commissions.map(c => c.lender || c.carrier).filter(Boolean))].map(item => (
                        <SelectItem key={item} value={item}>{item}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Sort By</Label>
                  <Select value={sortBy} onValueChange={setSortBy}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {REPORT_COLUMNS.map(col => (
                        <SelectItem key={col.key} value={col.key}>{col.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-sm font-medium mb-2 block">Sort Order</Label>
                  <Select value={sortOrder} onValueChange={setSortOrder}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="asc">Ascending</SelectItem>
                      <SelectItem value="desc">Descending</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Preview */}
              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-sm font-medium">Preview (First 10 rows)</Label>
                  <Badge variant="outline">{commissions.length} total records</Badge>
                </div>
                <div className="border rounded-lg overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        {selectedColumns.map(col => {
                          const colDef = REPORT_COLUMNS.find(c => c.key === col);
                          return (
                            <TableHead key={col}>{colDef?.label || col}</TableHead>
                          );
                        })}
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {commissions
                        .filter(c => {
                          if (agentFilter !== 'all' && c.agent_id !== agentFilter) return false;
                          if (lenderFilter !== 'all' && (c.lender || c.carrier) !== lenderFilter) return false;
                          return true;
                        })
                        .sort((a, b) => {
                          const aVal = a[sortBy] || '';
                          const bVal = b[sortBy] || '';
                          if (sortOrder === 'asc') return aVal > bVal ? 1 : -1;
                          return aVal < bVal ? 1 : -1;
                        })
                        .slice(0, 10)
                        .map((row, idx) => (
                          <TableRow key={row.id || idx}>
                            {selectedColumns.map(col => {
                              const colDef = REPORT_COLUMNS.find(c => c.key === col);
                              let value = row[col];
                              if (colDef?.type === 'currency' && value) {
                                value = formatCurrency(value);
                              } else if (colDef?.type === 'date' && value) {
                                value = new Date(value).toLocaleDateString();
                              }
                              return (
                                <TableCell key={col}>{value || '-'}</TableCell>
                              );
                            })}
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
